import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { 
  useGetApiRequestLogsQuery, 
  useExportApiRequestLogsMutation,
  LogsFilters 
} from '../../store/api/logsApi';
import { Card, Button } from '../../styles/components';
import { LogDetailModal } from './LogDetailModal';
import { LogsFiltersComponent } from './LogsFilters';
import {
  ClockIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const LogsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
  max-width: 1400px;
  margin: 2rem auto;
  width: 100%;
  padding: 0 2rem;
`;

const LogsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderContent = styled.div``;

const LogsTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.gray[900]};
`;

const LogsSubtitle = styled.p`
  margin-top: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.gray[500]};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
`;

const ExportButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background-color: ${props => props.theme.colors.gray[100]};
  color: ${props => props.theme.colors.gray[700]};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.gray[200]};
  }
  
  svg {
    height: 1rem;
    width: 1rem;
  }
`;

const SearchButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  white-space: nowrap;

  svg {
    height: 1rem;
    width: 1rem;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const ResultsCount = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSizes.sm};
  
  svg {
    height: 1rem;
    width: 1rem;
  }
`;

const LogsTableCard = styled(Card)`
  padding: 0;
`;

const Table = styled.table`
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

const TableHead = styled.thead`
  background-color: ${props => props.theme.colors.gray[50]};
`;

const TableHeader = styled.th`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.xl};
  text-align: left;
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.gray[500]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TableBody = styled.tbody`
  background-color: white;
`;

const TableRow = styled.tr`
  border-top: 1px solid ${props => props.theme.colors.gray[200]};
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.theme.colors.gray[50]};
  }
`;

const TableCell = styled.td`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  white-space: nowrap;
`;

const ClientId = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.primary[600]};
  cursor: pointer;
  
  &:hover {
    color: ${props => props.theme.colors.primary[900]};
    text-decoration: underline;
  }
`;

const StatusBadge = styled.span<{ $status: number }>`
  display: inline-flex;
  align-items: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.sm};
  
  ${props => {
    if (props.$status >= 200 && props.$status < 300) {
      return `
        background-color: ${props.theme.colors.success[50]};
        color: ${props.theme.colors.success[600]};
      `;
    } else if (props.$status >= 400 && props.$status < 500) {
      return `
        background-color: ${props.theme.colors.warning[50]};
        color: ${props.theme.colors.warning[600]};
      `;
    } else if (props.$status >= 500) {
      return `
        background-color: ${props.theme.colors.error[50]};
        color: ${props.theme.colors.error[600]};
      `;
    } else {
      return `
        background-color: ${props.theme.colors.gray[50]};
        color: ${props.theme.colors.gray[600]};
      `;
    }
  }}
`;

const MethodBadge = styled.span<{ $method: string }>`
  display: inline-flex;
  align-items: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.sm};
  
  ${props => {
    switch (props.$method) {
      case 'GET':
        return `
          background-color: ${props.theme.colors.primary[50]};
          color: ${props.theme.colors.primary[600]};
        `;
      case 'POST':
        return `
          background-color: ${props.theme.colors.success[50]};
          color: ${props.theme.colors.success[600]};
        `;
      case 'PUT':
        return `
          background-color: ${props.theme.colors.warning[50]};
          color: ${props.theme.colors.warning[600]};
        `;
      case 'DELETE':
        return `
          background-color: ${props.theme.colors.error[50]};
          color: ${props.theme.colors.error[600]};
        `;
      default:
        return `
          background-color: ${props.theme.colors.gray[50]};
          color: ${props.theme.colors.gray[600]};
        `;
    }
  }}
`;

const ExecutionTime = styled.span<{ $slow?: boolean }>`
  ${props => props.$slow && `
    color: ${props.theme.colors.error[600]};
    font-weight: ${props.theme.fontWeights.medium};
  `}
`;

const LoadingCard = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 16rem;
  color: ${props => props.theme.colors.gray[500]};
`;

const EmptyCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing['2xl']};
  text-align: center;
  color: ${props => props.theme.colors.gray[500]};
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.md};
  background: white;
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
`;

const PaginationInfo = styled.div`
  flex: 1;
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const PageButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  min-width: 2.5rem;
  height: 2.5rem;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  background: ${props => props.$active ? props.theme.colors.primary[50] : 'white'};
  color: ${props => props.$active ? props.theme.colors.primary[600] : props.theme.colors.gray[500]};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary[100] : props.theme.colors.gray[50]};
    border-color: ${props => props.$active ? props.theme.colors.primary[300] : props.theme.colors.gray[300]};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    height: 1.25rem;
    width: 1.25rem;
  }
`;

// Format a Date to the `YYYY-MM-DDTHH:mm` shape a <input type="datetime-local"> expects (local time).
const toDateTimeLocal = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function LogsPage() {
  const [searchParams] = useSearchParams();
  const clientIdFromUrl = searchParams.get('clientId');
  
  const now = new Date();
  const defaultStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  defaultStart.setHours(0, 0, 0, 0);           // 7 days ago, start of day
  const defaultEnd = new Date(now);
  defaultEnd.setHours(23, 59, 0, 0);           // today, end of day

  const initialFilters: LogsFilters = {
    page: 1,
    pageSize: 20,
    clientId: clientIdFromUrl || undefined,
    startDate: toDateTimeLocal(defaultStart),
    endDate: toDateTimeLocal(defaultEnd)
  };

  const [page, setPage] = useState(1);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  // `filters` = the draft edited in the UI; `appliedFilters` = what the query actually runs.
  // Logs are only fetched once the user presses Search (or when drilling in with a clientId).
  const [filters, setFilters] = useState<LogsFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<LogsFilters>(initialFilters);
  const [hasSearched, setHasSearched] = useState<boolean>(!!clientIdFromUrl);

  // When arriving from a client's "View Logs" link (clientId in the URL), auto-search that client.
  useEffect(() => {
    if (clientIdFromUrl) {
      setFilters(prev => ({ ...prev, clientId: clientIdFromUrl }));
      setAppliedFilters(prev => ({ ...prev, clientId: clientIdFromUrl }));
      setPage(1);
      setHasSearched(true);
    }
  }, [clientIdFromUrl]);

  const handleFilterChange = (key: keyof LogsFilters, value: any) => {
    // Edit the draft only — no fetch until Search is pressed.
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    setPage(1);
    setAppliedFilters({ ...filters, page: 1 });
    setHasSearched(true);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleLogClick = (logId: string) => {
    setSelectedLogId(logId);
  };

  const handleCloseModal = () => {
    setSelectedLogId(null);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const result = await exportLogs({
        ...filters,
        page: undefined,
        pageSize: undefined
      }).unwrap();
      
      const url = window.URL.createObjectURL(result);
      const link = document.createElement('a');
      link.href = url;
      link.download = `api_request_logs_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const queryParams = {
    ...appliedFilters,
    page,
  };

  // Skip the request until the user has explicitly searched (or drilled in via clientId).
  const { data, isFetching, error } = useGetApiRequestLogsQuery(queryParams, { skip: !hasSearched });
  const [exportLogs] = useExportApiRequestLogsMutation();

  return (
    <LogsContainer>
      <LogsHeader>
        <HeaderContent>
          <LogsTitle>API Request Logs</LogsTitle>
          <LogsSubtitle>
            Monitor and analyze API request activity
          </LogsSubtitle>
        </HeaderContent>
        <HeaderActions>
          <ExportButton onClick={handleExport} disabled={isExporting}>
            <ArrowDownTrayIcon />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </ExportButton>
        </HeaderActions>
      </LogsHeader>

      <FilterContainer>
        <FilterGroup>
          <LogsFiltersComponent
            filters={filters}
            onFilterChange={handleFilterChange}
          />
          <SearchButton onClick={handleSearch} disabled={isFetching}>
            <MagnifyingGlassIcon />
            Search
          </SearchButton>
        </FilterGroup>

        {hasSearched && (
          <ResultsCount>
            <FunnelIcon />
            {data?.totalCount || 0} API requests
          </ResultsCount>
        )}
      </FilterContainer>
      
      {!hasSearched ? (
        <EmptyCard>
          <p>Use the filters above and press <strong>Search</strong> to view logs.</p>
        </EmptyCard>
      ) : isFetching ? (
        <LoadingCard>Loading logs...</LoadingCard>
      ) : !data?.items.length ? (
        <EmptyCard>
          <p>No logs found</p>
        </EmptyCard>
      ) : (
        <>
          <LogsTableCard>
            <Table>
              <TableHead>
                <tr>
                  <TableHeader>Timestamp</TableHeader>
                  <TableHeader>Client</TableHeader>
                  <TableHeader>Endpoint</TableHeader>
                  <TableHeader>Method</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Time</TableHeader>
                  <TableHeader>Provider</TableHeader>
                </tr>
              </TableHead>
              <TableBody>
                {data.items.map((log) => (
                  <TableRow key={log.id} onClick={() => handleLogClick(log.id)}>
                    <TableCell>
                      {new Date(log.timestamp).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })} {new Date(log.timestamp).toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false
                      })}
                    </TableCell>
                    <TableCell>
                      <ClientId>{log.clientId}</ClientId>
                    </TableCell>
                    <TableCell>{log.endpoint}</TableCell>
                    <TableCell>
                      <MethodBadge $method={log.httpMethod}>{log.httpMethod}</MethodBadge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge $status={log.statusCode}>{log.statusCode}</StatusBadge>
                    </TableCell>
                    <TableCell>
                      <ExecutionTime $slow={log.executionTimeMs > 1000}>
                        {log.executionTimeMs}ms
                      </ExecutionTime>
                    </TableCell>
                    <TableCell>{log.providerName || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </LogsTableCard>
          
          <PaginationContainer>
            <PaginationInfo>
              Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, data.totalCount)} of {data.totalCount} results
            </PaginationInfo>
            
            <PaginationControls>
              <PageButton
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <ChevronLeftIcon />
              </PageButton>
              
              {Array.from({ length: Math.min(5, data.totalPages || 1) }, (_, i) => {
                const pageNumber = Math.max(1, page - 2) + i;
                if (pageNumber > (data.totalPages || 1)) return null;
                
                return (
                  <PageButton
                    key={pageNumber}
                    $active={pageNumber === page}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </PageButton>
                );
              })}
              
              <PageButton
                onClick={() => handlePageChange(page + 1)}
                disabled={page === (data.totalPages || 1)}
              >
                <ChevronRightIcon />
              </PageButton>
            </PaginationControls>
          </PaginationContainer>
        </>
      )}

      {selectedLogId && (
        <LogDetailModal 
          logId={selectedLogId}
          isOpen={!!selectedLogId}
          onClose={handleCloseModal}
        />
      )}
    </LogsContainer>
  );
}